import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import { RichTextEditorComponent } from '@syncfusion/ej2-react-richtexteditor'

interface RowHoverToolbarState {
  visible: boolean
  top: number
  left: number
  canMoveUp: boolean
  canMoveDown: boolean
}

interface UseEmailRowToolbarArgs {
  toolbarRef: RefObject<RichTextEditorComponent>
  isEmailTemplate: boolean
  isEditing: boolean
  contentHtml: string
  onRowsMutated: () => void
}

interface UseEmailRowToolbarResult {
  rowHoverToolbar: RowHoverToolbarState
  moveHoveredRow: (direction: 'up' | 'down') => void
  deleteHoveredRow: () => void
  clearRowHoverToolbar: () => void
  lockRowHoverToolbar: () => void
  unlockRowHoverToolbar: () => void
}

const useEmailRowToolbar = ({
  toolbarRef,
  isEmailTemplate,
  isEditing,
  contentHtml,
  onRowsMutated,
}: UseEmailRowToolbarArgs): UseEmailRowToolbarResult => {
  const hoveredRowRef = useRef<HTMLElement | null>(null)
  const rowToolbarLockedRef = useRef(false)
  const hideRowToolbarTimerRef = useRef<number | null>(null)
  const [rowHoverToolbar, setRowHoverToolbar] = useState<RowHoverToolbarState>({
    visible: false,
    top: 0,
    left: 0,
    canMoveUp: false,
    canMoveDown: false,
  })

  const clearHideRowToolbarTimer = (): void => {
    if (hideRowToolbarTimerRef.current) {
      window.clearTimeout(hideRowToolbarTimerRef.current)
      hideRowToolbarTimerRef.current = null
    }
  }

  const hideRowHoverToolbarNow = (): void => {
    hoveredRowRef.current = null
    setRowHoverToolbar((prev) => ({ ...prev, visible: false }))
  }

  const isEmailRowElement = (el: Element | null): el is HTMLElement =>
    !!el && el.classList.contains('e-rte-email-row')

  const clearRowHoverToolbar = useCallback((): void => {
    clearHideRowToolbarTimer()
    hideRowHoverToolbarNow()
  }, [])

  const scheduleHideRowHoverToolbar = useCallback((): void => {
    clearHideRowToolbarTimer()
    hideRowToolbarTimerRef.current = window.setTimeout(() => {
      if (!rowToolbarLockedRef.current) {
        hideRowHoverToolbarNow()
      }
      hideRowToolbarTimerRef.current = null
    }, 120)
  }, [])

  const lockRowHoverToolbar = useCallback((): void => {
    rowToolbarLockedRef.current = true
    clearHideRowToolbarTimer()
  }, [])

  const unlockRowHoverToolbar = useCallback((): void => {
    rowToolbarLockedRef.current = false
    scheduleHideRowHoverToolbar()
  }, [scheduleHideRowHoverToolbar])

  const updateRowHoverToolbarPosition = useCallback(
    (row: HTMLElement): void => {
      const ownerDoc = row.ownerDocument
      const rowRect = row.getBoundingClientRect()
      let top = rowRect.top + 6
      let left = rowRect.right - 86

      if (ownerDoc !== document) {
        const iframe = toolbarRef.current?.element?.querySelector(
          'iframe',
        ) as HTMLIFrameElement | null
        if (iframe) {
          const iframeRect = iframe.getBoundingClientRect()
          top = iframeRect.top + rowRect.top + 6
          left = iframeRect.left + rowRect.right - 86
        }
      }

      const prevRow = isEmailRowElement(row.previousElementSibling)
      const nextRow = isEmailRowElement(row.nextElementSibling)
      setRowHoverToolbar({
        visible: true,
        top,
        left,
        canMoveUp: !!prevRow,
        canMoveDown: !!nextRow,
      })
    },
    [toolbarRef],
  )

  const moveHoveredRow = useCallback(
    (direction: 'up' | 'down'): void => {
      const row = hoveredRowRef.current
      const parent = row?.parentElement
      if (!row || !parent) return

      const sibling =
        direction === 'up' ? row.previousElementSibling : row.nextElementSibling
      if (!isEmailRowElement(sibling)) return

      if (direction === 'up') {
        parent.insertBefore(row, sibling)
      } else {
        parent.insertBefore(sibling, row)
      }
      updateRowHoverToolbarPosition(row)
      onRowsMutated()
    },
    [onRowsMutated, updateRowHoverToolbarPosition],
  )

  const deleteHoveredRow = useCallback((): void => {
    const row = hoveredRowRef.current
    if (!row) return
    row.remove()
    clearRowHoverToolbar()
    onRowsMutated()
  }, [clearRowHoverToolbar, onRowsMutated])

  useEffect(() => {
    if (!isEmailTemplate || !isEditing) {
      clearRowHoverToolbar()
      return
    }
    let panel: HTMLElement | null = null
    let attempts = 0
    const onMouseMove = (e: MouseEvent): void => {
      if (rowToolbarLockedRef.current) return
      const target = e.target as HTMLElement | null
      if (!target) return
      const row = target.closest('.e-rte-email-row') as HTMLElement | null
      if (!row) {
        scheduleHideRowHoverToolbar()
        return
      }
      hoveredRowRef.current = row
      updateRowHoverToolbarPosition(row)
    }
    const onMouseLeave = (): void => {
      scheduleHideRowHoverToolbar()
    }

    const pollId = window.setInterval(() => {
      attempts += 1
      const rte = toolbarRef.current
      const nextPanel = rte?.contentModule?.getEditPanel?.() as
        | HTMLElement
        | undefined
      if (!nextPanel) {
        if (attempts > 80) clearInterval(pollId)
        return
      }
      panel = nextPanel
      panel.addEventListener('mousemove', onMouseMove)
      panel.addEventListener('mouseleave', onMouseLeave)
      clearInterval(pollId)
    }, 80)

    return () => {
      clearInterval(pollId)
      clearHideRowToolbarTimer()
      panel?.removeEventListener('mousemove', onMouseMove)
      panel?.removeEventListener('mouseleave', onMouseLeave)
    }
  }, [
    isEmailTemplate,
    isEditing,
    contentHtml,
    scheduleHideRowHoverToolbar,
    clearRowHoverToolbar,
    updateRowHoverToolbarPosition,
    toolbarRef,
  ])

  return {
    rowHoverToolbar,
    moveHoveredRow,
    deleteHoveredRow,
    clearRowHoverToolbar,
    lockRowHoverToolbar,
    unlockRowHoverToolbar,
  }
}

export default useEmailRowToolbar
